import {
  AppShellProvider,
  ProtectedRoute,
  WorkspaceLayout,
  WorkspaceRedirector,
} from '@agentic-flow-studio/shared/components/layout/AppShell';
import {
  AiProviderKeysSettingsSection,
  AppearanceSettingsSection,
  BillingSettingsSection,
  FeaturesSettingsSection,
  MembersSettingsSection,
  NotificationSettingsSection,
} from '@agentic-flow-studio/shared/components/settings/SettingsPage';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { AppSidebar } from '@/components/app-sidebar';
import { buildNavItems } from '@/lib/nav-items';
import AcceptInvite from '@/pages/AcceptInvite';
import Anchors from '@/pages/Anchors';
import Calendar from '@/pages/Calendar';
import Contacts from '@/pages/Contacts';
import ContactTypes from '@/pages/ContactTypes';
import Dashboard from '@/pages/Dashboard';
import EditContactType from '@/pages/EditContactType';
import ForgotPassword from '@/pages/ForgotPassword';
import Login from '@/pages/Login';
import Notifications from '@/pages/Notifications';
import OAuthComplete from '@/pages/OAuthComplete';
import Profile from '@/pages/Profile';
import Register from '@/pages/Register';
import ResetPassword from '@/pages/ResetPassword';
import Settings from '@/pages/Settings';
import Tasks from '@/pages/Tasks';
import Users from '@/pages/Users';

function App() {
  return (
    <AppShellProvider>
      <Router>
        <Routes>
          <Route path="/" element={<WorkspaceRedirector />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/oauth-complete" element={<OAuthComplete />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Catch-all redirects for non-prefixed routes */}
          <Route path="/dashboard" element={<WorkspaceRedirector />} />
          <Route path="/contacts" element={<WorkspaceRedirector />} />
          <Route path="/users" element={<WorkspaceRedirector />} />
          <Route path="/anchors" element={<WorkspaceRedirector />} />
          <Route path="/contact-types" element={<WorkspaceRedirector />} />
          <Route path="/tasks" element={<WorkspaceRedirector />} />
          <Route path="/calendar" element={<WorkspaceRedirector />} />
          <Route path="/profile" element={<WorkspaceRedirector />} />
          {/* Stripe Checkout/Billing Portal returns land here bare (no live session to resolve
              an org/workspace id) — see BillingService.settingsReturnUrl. Point straight at
              billing rather than the settings index (which redirects to appearance and would
              drop the ?upgrade=success/?session_id query string on that second hop). */}
          <Route
            path="/settings"
            element={<WorkspaceRedirector defaultPath="settings/billing" />}
          />
          <Route path="/notifications" element={<WorkspaceRedirector />} />

          {/* Main App Workspace Routes (with AppSidebar) */}
          <Route
            path="/org/:orgId/workspace/:workspaceId"
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  SidebarComponent={AppSidebar}
                  buildDestinations={buildNavItems}
                  productKey="anchor_contact"
                />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="contacts" element={<Contacts />}>
              <Route path=":contactId" element={<Outlet />} />
              <Route path="edit/:contactId" element={<Outlet />} />
            </Route>
            <Route path="users" element={<Users />} />
            <Route
              path="contact-types"
              element={<Navigate to="../settings/contact-types" replace />}
            />
            <Route
              path="contact-types/:id/edit"
              element={<Navigate to="../../settings/contact-types" replace />}
            />
            <Route path="calendar" element={<Calendar />} />
            <Route path="anchors" element={<Anchors />}>
              <Route path=":anchorId" element={<Outlet />} />
            </Route>
            <Route path="tasks" element={<Tasks />}>
              <Route path=":taskId" element={<Outlet />} />
              <Route path="edit/:taskId" element={<Outlet />} />
            </Route>
            <Route path="profile" element={<Profile />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Settings Full-Page Takeover Route (SettingsSidebar ONLY) */}
          <Route
            path="/org/:orgId/workspace/:workspaceId/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          >
            {/* No Accounts tab: Skill Viper uses Google only to sign in, never to attach a mailbox. */}
            <Route index element={<Navigate to="appearance" replace />} />
            <Route path="accounts" element={<Navigate to="../appearance" replace />} />
            <Route path="contact-types" element={<ContactTypes />} />
            <Route path="contact-types/:id/edit" element={<EditContactType />} />
            <Route path="appearance" element={<AppearanceSettingsSection />} />
            <Route path="notifications" element={<NotificationSettingsSection />} />
            <Route path="members" element={<MembersSettingsSection />} />
            <Route path="features" element={<FeaturesSettingsSection />} />
            <Route path="ai-provider" element={<AiProviderKeysSettingsSection />} />
            <Route path="billing" element={<BillingSettingsSection />} />
          </Route>
        </Routes>
      </Router>
    </AppShellProvider>
  );
}

export default App;
